
import React, { useEffect, useRef, useState } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';

interface ScrollRevealProps {
  children: React.ReactNode;
  animation?: 'fade' | 'slide' | 'scale' | 'rotate' | 'parallax';
  direction?: 'up' | 'down' | 'left' | 'right';
  delay?: number;
  duration?: number;
  offset?: number;
  className?: string;
}

export const ScrollReveal = ({
  children,
  animation = 'fade',
  direction = 'up',
  delay = 0,
  duration = 0.8,
  offset = 0.1,
  className
}: ScrollRevealProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: offset, once: true });
  const controls = useAnimation();

  const getInitialState = () => {
    switch (animation) {
      case 'slide':
        return {
          opacity: 0,
          x: direction === 'left' ? -100 : direction === 'right' ? 100 : 0,
          y: direction === 'up' ? 100 : direction === 'down' ? -100 : 0
        };
      case 'scale':
        return { opacity: 0, scale: 0.8 };
      case 'rotate':
        return { opacity: 0, rotateY: -90, scale: 0.8 };
      case 'parallax':
        return { opacity: 0, y: 50, scale: 0.95 };
      default:
        return { opacity: 0, y: 30 };
    }
  };

  const getAnimateState = () => {
    switch (animation) {
      case 'slide':
      case 'fade':
        return { opacity: 1, x: 0, y: 0 };
      case 'scale':
        return { opacity: 1, scale: 1 };
      case 'rotate':
        return { opacity: 1, rotateY: 0, scale: 1 };
      case 'parallax':
        return { opacity: 1, y: 0, scale: 1 };
      default:
        return { opacity: 1, y: 0 };
    }
  };

  useEffect(() => {
    if (isInView) {
      controls.start(getAnimateState());
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={getInitialState()}
      animate={controls}
      transition={{
        duration,
        delay,
        ease: 'easeOut',
        type: animation === 'rotate' ? 'spring' : 'tween',
        stiffness: animation === 'rotate' ? 100 : undefined,
        damping: animation === 'rotate' ? 15 : undefined
      }}
    >
      {children}
    </motion.div>
  );
};
